import { CSSProperties, Fragment, useMemo } from 'react';
import {
  deserify,
  GeneoKatex,
  getTeacherSubjectName,
  PrintableQuestion,
} from '@geneo2-web/shared-ui';

import {
  MarkingSchemeEnum,
  MultiSubjectSection,
  MultiSubjectSubSection,
  OMRSectionInfo,
  SectionQuestionMetaData,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';

import { Question } from '@protos/content_management/content.db_pb';
import { useAppSelector } from 'apps/teacher/src/reduxStore/reduxHooks';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';

const printableSx: CSSProperties = {
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
};

const stylesHtml: { [key: string]: CSSProperties } = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    padding: '10px 20px 10px',
    ...printableSx,
  },
  ellipsisText: {
    fontFamily: 'Poppins',
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: '15px',
    marginBottom: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  sectionContainer: {
    marginTop: '5px',
    display: 'flex',
    flexDirection: 'column',
    gap: '60px',
  },
  subjectSectionDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  subSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  subsectionBlock: {
    borderRadius: '15px',
    overflow: 'hidden',
  },
  subsectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: '10px',
    paddingBottom: '10px',
    boxSizing: 'border-box',
  },
  subsectionDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    marginTop: '10px',
    justifyContent: 'space-between',
    ...printableSx,
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    ...printableSx,
  },
  subsectionContent: {
    paddingTop: '10px',
  },
  questionsColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  questionBlock: {
    display: 'flex',
    flexDirection: 'column',
  },
  questionBlockWithAlts: {
    padding: '18px 13px',
    display: 'flex',
    flexDirection: 'column',
  },
  instructionText: {
    fontFamily: 'Poppins',
    marginTop: '5px',
    marginBottom: '5px',
    fontSize: '16px',
    ...printableSx,
  },
  orBox: {
    padding: '10px 20px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    ...printableSx,
  },
  orBadge: {
    color: '#000',
    padding: '10px',
    borderRadius: '50%',
    zIndex: 1,
    ...printableSx,
  },
  iconImg: {
    width: 20,
    height: 20,
    flexShrink: 0,
    display: 'inline-block',
    verticalAlign: 'middle',
    objectFit: 'contain',
    ...printableSx,
  },
  orLine: {
    height: '1px',
    width: '100%',
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    ...printableSx,
  },
  noContent: {
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    fontFamily: 'Poppins',
    ...printableSx,
  },
};

interface IPrintableAssessmentHtml {
  showAnswer?: boolean;
  assessmentContent?: AssessmentContent;
  // questions?: Question[];
  setName?: string;
}

export const PrintableAssessmentHtml = (props: IPrintableAssessmentHtml) => {
  const { assessmentContent, showAnswer, setName } = props;
  const ICONS_BASE = '/assets/shared-ui/icons';

  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const questionTypeAndMarks = assessmentContent?.questionTypeAndMarks ?? [];

  // ----------------------------------------------------

  const getCustomSchemeMarks = (): number | undefined => {
    if (
      assessmentContent?.assessment?.markingSchemeType ===
      MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
    ) {
      return assessmentContent?.questionsSequenceInfo?.marksPerQuestion ?? 1;
    }
    return undefined;
  };

  // ----------------------------------------------------

  const selectedOmrSet =
    assessmentContent?.questionsSequenceInfo?.omrSetInfo.find(
      (set: any) => set.omrSetName === setName
    );

  // ----------------------------------------------------
  /**
    Fallback used when a section has no `multiSubjectSubSections` .
   **/
  const getFallbackSectionQuestions = (
    section: MultiSubjectSection
  ): { question: Question; questionNumber: number }[] => {
    const omrSection = selectedOmrSet?.omrSectionInfo.find(
      (s: OMRSectionInfo) => s.subjectId === section.subjectId
    );

    const orderedInfo = (omrSection?.omrQuestionsSequenceInfo ?? [])
      .slice()
      .sort((a: any, b: any) => (a?.sequence ?? 0) - (b?.sequence ?? 0));

    return orderedInfo
      .map((item: any) => {
        const q = getFormattedQuestionFromId(item.questionId);
        if (!q) return null;
        const marks = getMarksForQuestionId(item.questionId);
        if (q.question?.model?.value?.commonQuestionContent) {
          q.question.model.value.commonQuestionContent.marks = [marks];
          const taskInfo = taskQuestionInfoOmr.find(
            (info) => info.questionId === item.questionId
          );
          if (typeof taskInfo?.estimatedTimeInMin === 'number') {
            q.question.model.value.commonQuestionContent.time =
              taskInfo.estimatedTimeInMin * 60;
          }
        }
        const questionNumber = getQuestionNumberFromMultiSubject(
          item.questionId
        );
        return { question: q, questionNumber };
      })
      .filter(
        (x): x is { question: Question; questionNumber: number } => x != null
      );
  };
  // ----------------------------------------------------
  const definedSections = assessmentContent?.multiSubjectSections ?? [];
  const sections: MultiSubjectSection[] =
    definedSections.length > 0
      ? definedSections
      : (selectedOmrSet?.omrSectionInfo ?? []).map(
          (omrSection: OMRSectionInfo) =>
            ({
              subjectId: omrSection.subjectId,
              sectionName: '',
              sectionTitle: '',
              instructions: [],
              multiSubjectSubSections: [],
            } as unknown as MultiSubjectSection)
        );

  const taskQuestionInfoOmr = useMemo(() => {
    if (!assessmentContent?.questionsSequenceInfo?.omrSetInfo) {
      return [];
    }
    if (!selectedOmrSet) {
      return [];
    }
    return selectedOmrSet.omrSectionInfo.flatMap((section: any) =>
      section.omrQuestionsSequenceInfo.map((item: any) => ({
        questionId: item.questionId,
        sequence: item.sequence,
      }))
    );
  }, [selectedOmrSet]);

  // ----------------------------------------------------

  const questionMap = useMemo(() => {
    const map = new Map<string, Question>();
    assessmentContent?.assessmentContent.forEach((q) => {
      const question = new Question(q);
      if (question.questionId) map.set(String(question.questionId), question);
    });
    return map;
  }, [assessmentContent]);

  const omrQuestionSequenceMap = useMemo(() => {
    const map = new Map<string, number>();
    selectedOmrSet?.omrSectionInfo.forEach((section: OMRSectionInfo) => {
      section.omrQuestionsSequenceInfo?.forEach((question: any) => {
        map.set(question.questionId, question.sequence);
      });
    });

    return map;
  }, [selectedOmrSet]);
  // ----------------------------------------------------
  const getMarksForQuestionId = (questionId?: string): number => {
    if (!questionId) return 0;
    const taskQuestionSquenceInfo =
      assessmentContent?.questionsSequenceInfo?.taskQuestionInfo.find(
        (info) => info.questionId === questionId
      );
    const customSchemeMarks = getCustomSchemeMarks();
    if (customSchemeMarks !== undefined)
      return taskQuestionSquenceInfo?.maxMarks ?? customSchemeMarks;
    else return taskQuestionSquenceInfo?.maxMarks ?? 0;
    // const ques = questionMap.get(questionId);
    // const taskInfo = taskQuestionInfoOmr.find(
    //   (info) => info.questionId === questionId
    // );
    // const quesType = ques?.questionType;
    // const foundQuesType = questionTypeAndMarks.find(
    //   (qType) => qType.questionType === quesType
    // );
    // if (foundQuesType) return foundQuesType.marks ?? 0;
    // if (typeof taskInfo?.maxMarks === 'number') return taskInfo.maxMarks;
    // const existing =
    //   ques?.question?.model?.value?.commonQuestionContent?.marks?.[0];
    // if (typeof existing === 'number') return existing;
    // return 0;
  };

  // ----------------------------------------------------

  const getFormattedQuestionFromId = (id: string): Question | undefined => {
    const ques = questionMap.get(id);
    return ques ?? undefined;
  };

  // ----------------------------------------------------

  const getMarksForQuestionEntry = (
    entry: { questionId: string; maxMarks?: number },
    subSection: MultiSubjectSubSection
  ): number => {
    const taskQuestionSquenceInfo =
      assessmentContent?.questionsSequenceInfo?.taskQuestionInfo.find(
        (info) => info.questionId === entry.questionId
      );
    if (typeof taskQuestionSquenceInfo?.maxMarks === 'number') {
      return taskQuestionSquenceInfo.maxMarks;
    }
    // Fallbacks only if no taskQuestionInfo entry exists for this question
    const customSchemeMarks = getCustomSchemeMarks();
    if (customSchemeMarks !== undefined)
      return taskQuestionSquenceInfo?.maxMarks ?? customSchemeMarks;

    if (typeof entry.maxMarks === 'number') return entry.maxMarks;
    const subSectionMarks = subSection.questionTypeAndMarks;
    const ques = questionMap.get(entry.questionId);
    const quesType = ques?.questionType;
    if (subSectionMarks?.length && quesType !== undefined) {
      const found = subSectionMarks.find(
        (qType) => qType.questionType === quesType
      );
      if (found && typeof found.marks === 'number') return found.marks;
    }
    return getMarksForQuestionId(entry.questionId);
  };

  // ----------------------------------------------------

  const subsectionTotalMarks = (subSection: MultiSubjectSubSection): number => {
    const meta = subSection?.subSectionMetaData;
    if (!meta?.length) return 0;
    return meta.reduce(
      (sum, entry) => sum + getMarksForQuestionEntry(entry, subSection),
      0
    );
  };

  // ----------------------------------------------------
  const getQuestionNumberFromMultiSubject = (questionId: string): number => {
    const taskInfo = taskQuestionInfoOmr.find(
      (info) => info.questionId === questionId
    );

    return taskInfo?.sequence ?? 0;
  };

  // ----------------------------------------------------

  const getQuestionsAndNumberForMeta = (params: {
    quesMeta: SectionQuestionMetaData;
    subSection: MultiSubjectSubSection;
    // sectionIndex: number;
    // subSectionIndex: number;
    // index: number;
  }): { questions: Question[]; questionNumber: number } | null => {
    const { quesMeta, subSection } = params;
    const allQuestionsMeta = [
      quesMeta,
      ...(quesMeta.alternateQuestions ?? []).sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
      ),
    ];
    const questions = allQuestionsMeta
      .map((ques) => {
        const q = getFormattedQuestionFromId(ques.questionId);
        if (!q) return undefined;
        const subSectionMarks = subSection.questionTypeAndMarks?.find(
          (val) => val.questionType === q.questionType
        );
        if (q.question?.model?.value?.commonQuestionContent) {
          const taskQuestionSquenceInfo =
            assessmentContent?.questionsSequenceInfo?.taskQuestionInfo.find(
              (info) => info.questionId === ques.questionId
            );
          // Source of truth: questionsSequenceInfo.taskQuestionInfo
          if (typeof taskQuestionSquenceInfo?.maxMarks === 'number') {
            q.question.model.value.commonQuestionContent.marks = [
              taskQuestionSquenceInfo.maxMarks,
            ];
          } else if (typeof ques.maxMarks === 'number') {
            q.question.model.value.commonQuestionContent.marks = [
              ques.maxMarks,
            ];
          } else if (subSectionMarks) {
            q.question.model.value.commonQuestionContent.marks = [
              subSectionMarks.marks ?? 0,
            ];
          } else {
            q.question.model.value.commonQuestionContent.marks = [
              getMarksForQuestionId(ques.questionId), // custom-scheme default / 0
            ];
          }

          const taskInfo = taskQuestionInfoOmr.find(
            (info) => info.questionId === ques.questionId
          );
          if (typeof taskInfo?.estimatedTimeInMin === 'number') {
            q.question.model.value.commonQuestionContent.time =
              taskInfo.estimatedTimeInMin * 60;
          }
        }
        return q;
      })
      .filter((q): q is Question => q != null);
    if (questions.length === 0) return null;
    const questionNumber = getQuestionNumberFromMultiSubject(
      quesMeta.questionId
    );
    return { questions, questionNumber };
  };

  // ----------------------------------------------------

  const getTotalMarksOfSection = (section: MultiSubjectSection): number => {
    const hasSubSections = (section.multiSubjectSubSections ?? []).length > 0;
    if (!hasSubSections) {
      return getFallbackSectionQuestions(section).reduce(
        (sum, item) =>
          sum +
          (item.question.question?.model?.value?.commonQuestionContent
            ?.marks?.[0] ?? 0),
        0
      );
    }
    return section.multiSubjectSubSections.reduce(
      (sectionTotal, subSection) =>
        sectionTotal + subsectionTotalMarks(subSection),
      0
    );
  };
  // ----------------------------------------------------

  const sectionLetter = (index: number) => String.fromCharCode(65 + index);
  const getSubjectName = (subjectId: number) =>
    getTeacherSubjectName(subjectId, user_info?.teachClassSubjects) ?? '';

  // ----------------------------------------------------

  if (!assessmentContent || sections.length === 0) {
    return (
      <div style={stylesHtml.root}>
        <p style={{ fontFamily: 'Poppins', color: '#666' }}>
          No paper content to preview.
        </p>
      </div>
    );
  }

  const totalMarks = sections.reduce((total, section) => {
    return total + getTotalMarksOfSection(section);
  }, 0);

  return (
    <div style={stylesHtml.root}>
      {user_info?.schoolDetails[0]?.schoolName && (
        <div
          style={{
            color: '#000',
            textAlign: 'center',
            display: 'block',
            padding: '5px 0',
            fontWeight: 'bold',
            ...printableSx,
          }}
          className="avoid-break-element"
        >
          {user_info?.schoolDetails[0]?.schoolName}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          ...printableSx,
        }}
        className="avoid-break-element"
      >
        {assessmentContent?.assessment?.class && (
          <div
            style={{
              color: '#000',
              textAlign: 'center',
              display: 'block',
              fontWeight: 'bold',
              ...printableSx,
            }}
            className="avoid-break-element"
          >
            {assessmentContent?.assessment?.class
              ? `GRADE ${assessmentContent?.assessment?.class}`
              : ''}
          </div>
        )}
      </div>
      <h3 style={{ ...stylesHtml.ellipsisText, marginTop: '0' }}>
        {assessmentContent?.assessment?.assessmentTitle}
      </h3>
      <h2 style={{ ...stylesHtml.ellipsisText, marginTop: '0' }}>
        SET- {selectedOmrSet?.omrSetName}
      </h2>
      {totalMarks && totalMarks > 0 && (
        <div
          style={{
            color: '#000',
            textAlign: 'center',
            display: 'block',
            fontWeight: 'bold',
            ...printableSx,
          }}
          className="avoid-break-element"
        >
          Total Marks: {totalMarks}
        </div>
      )}

      {assessmentContent?.assessment?.assessmentInstructions &&
        assessmentContent.assessment.assessmentInstructions.length > 0 &&
        assessmentContent.assessment.assessmentInstructions.some(Boolean) && (
          <div
            style={stylesHtml.instructionText}
            dangerouslySetInnerHTML={{
              __html: new GeneoKatex(
                assessmentContent.assessment.assessmentInstructions.join('')
              ).parsedHtml(),
            }}
          />
        )}

      <div style={stylesHtml.sectionContainer}>
        {sections.map((section, sectionIndex) => (
          <div key={`section-${sectionIndex}-${section.sectionName}`}>
            <div style={stylesHtml.subjectSectionDetails}>
              {sections.length > 1 && (
                <>
                  <div
                    style={{
                      fontFamily: 'Poppins',
                      color: '#007CDC',
                      margin: 0,
                      fontWeight: 'bold',
                    }}
                  >
                    Section {sectionLetter(sectionIndex)} -{' '}
                    {getSubjectName(section.subjectId)}
                    {section.sectionTitle ? ` - ${section.sectionTitle}` : ''}
                  </div>

                  {/* <p
                    style={{
                      fontFamily: 'Poppins',
                      color: '#0AA34F',
                      fontWeight: 'bold',
                      marginLeft: 'auto',
                      marginTop: '15px',
                      marginBottom: 0,
                    }}
                  >
                    Total: {getTotalMarksOfSection(section)} Marks
                  </p> */}
                </>
              )}

              {section.instructions?.length > 0 &&
                section.instructions.some(Boolean) && (
                  <p style={stylesHtml.instructionText}>
                    {section.instructions.join('\n')}
                  </p>
                )}
            </div>

            <div style={stylesHtml.subSectionContainer}>
              {!section.multiSubjectSubSections?.length
                ? // No subsections on this section -
                  (() => {
                    const fallbackQuestions =
                      getFallbackSectionQuestions(section);

                    if (!fallbackQuestions.length) {
                      return (
                        <div style={stylesHtml.noContent}>
                          No Questions Added
                        </div>
                      );
                    }

                    const fallbackTotalMarks = fallbackQuestions.reduce(
                      (sum, item) =>
                        sum +
                        (item.question.question?.model?.value
                          ?.commonQuestionContent?.marks?.[0] ?? 0),
                      0
                    );

                    return (
                      <>
                        <div style={stylesHtml.subsectionDetails}>
                          <div style={stylesHtml.detailsRow}>
                            <img
                              src={`${ICONS_BASE}/questions.png`}
                              alt=""
                              aria-hidden
                              style={stylesHtml.iconImg}
                            />
                            <span style={{ fontFamily: 'Poppins' }}>
                              Total No of Questions:{' '}
                              <b>{fallbackQuestions.length}</b>
                            </span>
                          </div>
                          <div style={stylesHtml.detailsRow}>
                            <img
                              src={`${ICONS_BASE}/percentage-primary.png`}
                              alt=""
                              aria-hidden
                              style={stylesHtml.iconImg}
                            />
                            <span style={{ fontFamily: 'Poppins' }}>
                              Marks: <b>{fallbackTotalMarks}</b>
                            </span>
                          </div>
                        </div>
                        <div style={stylesHtml.questionsColumn}>
                          {fallbackQuestions.map(
                            ({ question, questionNumber }) => (
                              <div
                                key={question.questionId}
                                style={stylesHtml.questionBlock}
                              >
                                <PrintableQuestion
                                  ques={question}
                                  number={questionNumber}
                                  // marks={
                                  //   question.question?.model?.value
                                  //     ?.commonQuestionContent?.marks?.[0]
                                  // }
                                  marks={getMarksForQuestionId(
                                    question?.questionId
                                  )}
                                  // marks={100}
                                  showAnswer={showAnswer}
                                  isAiGenerated={question?.isAiGenerated}
                                  showMedia={true}
                                />
                              </div>
                            )
                          )}
                        </div>
                      </>
                    );
                  })()
                : (section.multiSubjectSubSections ?? []).map(
                    (subSection, subSectionIndex) => (
                      <div
                        key={`sub-${sectionIndex}-${subSectionIndex}-${subSection.subSectionName}`}
                        style={stylesHtml.subsectionBlock}
                      >
                        <div style={stylesHtml.subsectionHeader}>
                          <div style={{ width: '100%' }}>
                            {(section.multiSubjectSubSections ?? []).length >
                              1 && (
                              <div
                                style={{
                                  fontFamily: 'Poppins',
                                  // fontSize: '26px',
                                  margin: 0,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  textDecoration: 'underline',
                                }}
                              >
                                {subSection.subSectionName}
                              </div>
                            )}
                            <div style={stylesHtml.subsectionDetails}>
                              <div style={stylesHtml.detailsRow}>
                                <img
                                  src={`${ICONS_BASE}/questions.png`}
                                  alt=""
                                  aria-hidden
                                  style={stylesHtml.iconImg}
                                />
                                <span style={{ fontFamily: 'Poppins' }}>
                                  Total No of Questions:{' '}
                                  <b>
                                    {subSection.subSectionMetaData?.length ?? 0}
                                  </b>
                                </span>
                              </div>
                              <div style={stylesHtml.detailsRow}>
                                <img
                                  src={`${ICONS_BASE}/percentage-primary.png`}
                                  alt=""
                                  aria-hidden
                                  style={stylesHtml.iconImg}
                                />
                                <span style={{ fontFamily: 'Poppins' }}>
                                  Marks:{' '}
                                  <b>{subsectionTotalMarks(subSection)}</b>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div style={stylesHtml.subsectionContent}>
                          <div style={stylesHtml.questionsColumn}>
                            {(subSection.subSectionMetaData ?? [])
                              .sort(
                                (a, b) =>
                                  (omrQuestionSequenceMap.get(a.questionId) ??
                                    0) -
                                  (omrQuestionSequenceMap.get(b.questionId) ??
                                    0)
                              )
                              .map((quesMeta, index) => {
                                const result = getQuestionsAndNumberForMeta({
                                  quesMeta,
                                  subSection,
                                });
                                if (!result) return null;
                                const { questions, questionNumber } = result;
                                const hasAlts = questions.length > 1;
                                return (
                                  <div
                                    key={quesMeta.questionId}
                                    style={
                                      hasAlts
                                        ? stylesHtml.questionBlockWithAlts
                                        : stylesHtml.questionBlock
                                    }
                                  >
                                    {questions.map((question, altIdx) => (
                                      <Fragment key={question.questionId}>
                                        <PrintableQuestion
                                          ques={question}
                                          number={
                                            altIdx === 0
                                              ? questionNumber
                                              : undefined
                                          }
                                          marks={
                                            altIdx === 0
                                              ? question.question?.model?.value
                                                  ?.commonQuestionContent
                                                  ?.marks?.[0]
                                              : undefined
                                          }
                                          showAnswer={showAnswer}
                                          isAiGenerated={
                                            question?.isAiGenerated
                                          }
                                          showMedia={true}
                                        />
                                        {altIdx < questions.length - 1 && (
                                          <div style={stylesHtml.orBox}>
                                            <div style={stylesHtml.orLine} />
                                            <div style={stylesHtml.orBadge}>
                                              OR
                                            </div>
                                          </div>
                                        )}
                                      </Fragment>
                                    ))}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    )
                  )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
